import React from "react";
import {
  translateService,
  stateService,
  routingService,
  signService,
  authService,
  workSpacesService,
  htmlHelperService,
  dateHelperService,
} from "../../services/imports";
import { BROWSER_AUTH_TYPES, documentCode as codeFromDigest } from "../../services/signService";
import { AppContext } from "../../Context/AppContext";
import OptimizedComponent from "../../components/optimizedComponent";
import { LoadingButton } from "../../components/formcontrols/imports";
import { stateEvents } from "../../models/states";
import IdentomatFrame from "../../components/faceSign/identomatFrame";
import PlacementView from "../../components/faceSign/placementView";
import linkSession from "../../services/linkSessionService";
import sessionService from "../../services/sessionService";
import signerManagement, { isCloudSigner } from "../../services/signerManagementService";
import { openSigningTools } from "../../services/dashboardModal";
import { CloudFlowNotice, CloudResultNotice } from "../../components/signers/cloudNotice";
import { signerMethod, methodTitle, methodKind, methodHint } from "../viewResult/signingMethods";
import customProtocolCheck from "protocol-checker";

const WARN_BEFORE_MS = 2 * 60 * 1000;
// the gateway asks Identomat at most every 10 s, and its callback fills in
// the outcome sooner; DONE still triggers an immediate check
const POLL_MS = 10000;
// failed checks in a row before the page says it cannot get the result
const CHECK_FAILURES_SHOWN = 3;
// while SIGNING, and while Unitool signs (EID_LAUNCHED): GET until SIGNED or
// SIGNING_FAILED
const SIGN_POLL_MS = 5000;
const POLLED_STATUSES = ["SIGNING", "EID_LAUNCHED"];
// the gateway's final statuses: nothing is signed after them
const FINAL_STATUSES = ["SIGNED", "SIGNING_FAILED", "CANCELLED", "EXPIRED", "SIGNATORY_MISMATCH", "VERIFICATION_UNDECIDED"];
// how long the page waits for the eID application to take the link
const EID_APP_WAIT_MS = 5000;
// the visible signature's size when the signing options give none, in pt
const DEFAULT_BOX = { width: 180, height: 60 };
// eID's stamp when the gateway gives no size (it answers 90 x 47 unless the
// share fixes one): the facsimile, and under it the stamp's line and date
const EID_BOX = { width: 90, height: 47 };
const EID_STAMP_LINE = "Digitally signed at www.validate.ge";
// statuses from which the process can still be cancelled; a rejected or
// expired verification only while another attempt may start (the gateway
// refuses an ended process: see cancellable)
const CANCELLABLE = [
  "INPUT_NEEDED", "INITIAL", "VERIFICATION_STARTED",
  "VERIFIED", "PLACED", "EID_LAUNCHED",
];


// in-app browsers (Facebook, Instagram, LINE, Android WebView) often block
// the camera in frames: suggest a real browser (face runbook F4.1)
const IN_APP_BROWSER = /FBAN|FBAV|Instagram|Line\/|; wv\)/i;

// The signing-process page (/sign/:processId).
// F1: shows the process and settles its signatory authentication — status
// INPUT_NEEDED offers the signer's signatoryAuthTypes, the pick goes out
// with PATCH (If-Match: last ETag).
// F4: with authType FACE, the biometric notice, then POST verification (no
// body) → the Identomat widget in an iframe with attempt and countdown.
// While VERIFICATION_STARTED the page polls POST verification/check (~3 s,
// and at once on Identomat's DONE) and renders the outcome by status:
// VERIFIED, VERIFICATION_REJECTED (+ rejectReason), SIGNATORY_MISMATCH,
// VERIFICATION_UNDECIDED, VERIFICATION_EXPIRED.
// F5: VERIFIED → placing the visible signature (PUT placement, If-Match; a
// fixed position is confirmed with an empty body; "without a visible
// signature" when it is not mandatory). F6: PLACED (or VERIFIED without a
// mark) → confirmation, re-read before Sign → POST finish → SIGNING, polled →
// SIGNED | SIGNING_FAILED. Cancel (POST cancel) until signing starts.
// Georgian eID (authType EID, SG6): no verification; from INITIAL the
// signature is placed (a fixed place confirmed, else optional), then POST eid
// hands out the geoeid-unitool:// link, opened as given → EID_LAUNCHED,
// polled until Unitool's signature makes it SIGNING, SIGNED or SIGNING_FAILED.
// Through a share the process can end SIGNATORY_MISMATCH (not the required
// signatory: a face check, or - since 2026-10-11 - the eID certificate's
// person; a seal card never meets one) or SIGNING_FAILED with GRANT_ENDED
// (the sharer changed the share).
export default class SignProcessPage extends OptimizedComponent {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      process: null,
      etag: null,
      loading: true,
      choosing: null,
      noticeRead: false,
      starting: false,
      verification: null, // {widgetUrl, expiresAt, attempt, maxAttempts}
      submitted: false, // Identomat said DONE
      now: Date.now(),
      cameraDenied: false,
      doc: null, // {name, docPath}
      docFile: null, // ArrayBuffer, loaded for placement and the document code
      documentCode: null, // face: "2DAC-8B8B-CBEC-FECB", from docFile's digest
      codeProblem: false, // face: no code (the bytes did not load, or not the gateway's)
      draft: null, // {page, x, y} being placed
      editing: false, // PLACED, moving the signature again
      noMark: false, // VERIFIED, signing without a visible signature
      busy: null, // "place" | "finish" | "cancel" | "eid"
      confirmCancel: false,
      eid: null, // {launchUrl, expiresAt}: only from the answer that made it
      eidNoApp: false, // the link opened nothing: the application is missing
      skipFacsimile: false, // eID: a fixed mark goes on with its text alone
      engines: null, // the catalogue's verification engines, for their labels
      // the gateway's set-up refusals (signer management, A2): the signing
      // tool not set up yet (its detail), its engine refusing or unreachable
      // ({ text, retry: "open" | "check" }), deactivated after the start
      notProvisioned: null,
      verificationProblem: null,
      checkBlocked: false,
      signerDeactivated: false,
    };
  }

  componentDidMount() {
    this.secureSubscription(
      translateService.subscribe(() => this.setState({ lang: translateService.key }))
    );
    this.loadProcess();
    this.checkCamera();
    // the engines' labels; a share link may not read the catalogue
    if (!linkSession.has())
      this.secureSubscription(
        signerManagement.catalogue().subscribe(
          (res) => this.setState({ engines: (res.data && res.data.engines) || [] }),
          () => {}
        )
      );
    const timer = setInterval(() => {
      if (this.state.verification || this.state.eid) this.setState({ now: Date.now() });
    }, 1000);
    // the outcome of the verification; paused while the tab is hidden
    const poll = setInterval(() => {
      if (!document.hidden && this.isVerifying() && !this.state.checkBlocked) this.checkVerification();
    }, POLL_MS);
    const signPoll = setInterval(() => {
      if (!document.hidden && this.state.process && POLLED_STATUSES.includes(this.state.process.status))
        this.loadProcess(true);
    }, SIGN_POLL_MS);
    this.secureSubscription({
      unsubscribe: () => {
        clearInterval(timer);
        clearInterval(poll);
        clearInterval(signPoll);
      },
    });
  }

  componentDidUpdate() {
    const wanted = this.needsPlacement() || (this.comparesCodes() && !this.state.codeProblem);
    if (wanted && this.state.doc && !this.state.docFile) this.loadDocumentBytes();
  }

  isEid = () => !!this.state.process && this.state.process.authType === "EID";

  // A face signing's consent, inside the verification, starts with the
  // document code, the signing tool and its key: the page shows the same
  // from before the verification starts until the process ends. eID has no
  // such consent.
  comparesCodes = () => {
    const p = this.state.process;
    return !!p && p.authType === "FACE" && !FINAL_STATUSES.includes(p.status);
  };

  needsPlacement = () => {
    const p = this.state.process;
    if (!p) return false;
    // eID verifies nobody first: its placement comes from INITIAL
    const ready = this.isEid() ? "INITIAL" : "VERIFIED";
    return (p.status === ready && !this.state.noMark) || (p.status === "PLACED" && this.state.editing);
  };

  isVerifying = () =>
    !!this.state.process && this.state.process.status === "VERIFICATION_STARTED";

  // one check at a time; the answer is the process with its new status
  checkVerification = () => {
    if (this.checking) return;
    this.checking = true;
    this.secureSubscription(
      signService.checkVerification(this.processId()).subscribe(
        (res) => {
          this.checking = false;
          this.checkFailures = 0;
          const process = res.data;
          const ended = process && process.status !== "VERIFICATION_STARTED";
          this.setState({
            process,
            etag: (res.headers && res.headers.etag) || this.state.etag,
            // the widget is over once there is an outcome
            verification: ended ? null : this.state.verification,
            submitted: ended ? false : this.state.submitted,
            checkTrouble: false,
          });
        },
        (e) => {
          this.checking = false;
          const status = e && e.response && e.response.status;
          // the engine refused the call over the signing tool's credentials:
          // stop asking, say who must act, retry only when asked
          if (signService.problemCode(e) === "VERIFICATION_ENGINE_REFUSED")
            return this.setState({ checkBlocked: true, verificationProblem: { text: this.refusedText(), retry: "check" } });
          // 409: nothing to check (the next poll will not run once the status
          // says so). Anything else (500, network): keep polling, and say so on
          // the page after a few failures in a row instead of a window each time
          if (status === 409) return;
          if (status === 404) return this.showError(e, "faceSign.loadError");
          this.checkFailures = (this.checkFailures || 0) + 1;
          if (this.checkFailures >= CHECK_FAILURES_SHOWN) this.setState({ checkTrouble: true });
        }
      )
    );
  };

  // a camera already denied for this site: say so before the widget starts
  checkCamera = () => {
    if (!navigator.permissions || !navigator.permissions.query) return;
    navigator.permissions
      .query({ name: "camera" })
      .then((status) => this.setState({ cameraDenied: status.state === "denied" }))
      .catch(() => {}); // Firefox and Safari may not know "camera"
  };

  startVerification = () => {
    if (this.state.starting) return;
    this.setState({ starting: true, verificationProblem: null, notProvisioned: null });
    this.secureSubscription(
      signService.startVerification(this.processId()).subscribe(
        (res) => {
          this.setState({
            verification: res.data,
            etag: (res.headers && res.headers.etag) || this.state.etag,
            submitted: false,
            starting: false,
            now: Date.now(),
          });
          // INITIAL → VERIFICATION_STARTED: show the new status
          if (this.state.process && this.state.process.status !== "VERIFICATION_STARTED")
            this.loadProcess(true);
        },
        (e) => {
          const code = signService.problemCode(e);
          if (code === "VERIFICATION_ENGINE_REFUSED")
            return this.setState({ starting: false, verificationProblem: { text: this.refusedText(), retry: "open" } });
          if (code === "VERIFICATION_ENGINE_UNAVAILABLE")
            return this.setState({
              starting: false,
              verificationProblem: { text: translateService.t("faceSign.engineUnreachable"), retry: "open" },
            });
          if (code === "SIGNER_NOT_PROVISIONED") return this.setState({ starting: false, notProvisioned: this.detailOf(e) });
          const exhausted = code === "VERIFICATION_ATTEMPTS_EXHAUSTED";
          this.setState({ starting: false, exhausted });
          this.showError(e, "faceSign.verificationError");
        }
      )
    );
  };

  // the document the process signs: its name for the page, its bytes for
  // placement (the same revision the process fixed; the gateway checked it)
  loadDocument = (process) => {
    if (!process || !process.docId || this.docRequested) return;
    this.docRequested = true;
    this.secureSubscription(
      workSpacesService.getDocumentResult(process.docId).subscribe((res) => {
        if (!res || !res.data) return;
        this.setState({ doc: { name: res.data.header || res.data.docName, docPath: res.data.docPath } });
      })
    );
  };

  loadDocumentBytes = () => {
    const doc = this.state.doc;
    if (!doc || !doc.docPath || this.bytesRequested) return;
    this.bytesRequested = true;
    htmlHelperService
      .getPdfBytes(doc.docPath)
      .then(async (docFile) => {
        // the code first: the viewer may take the bytes over
        const documentCode = this.comparesCodes() ? await this.codeOf(docFile) : null;
        this.setState({ docFile, documentCode, codeProblem: this.comparesCodes() && !documentCode });
      })
      .catch(() => {
        this.bytesRequested = false;
        if (this.comparesCodes()) this.setState({ codeProblem: true });
        stateService.event(stateEvents.globalError, translateService.t("faceSign.documentError"));
      });
  };

  // The document code of the bytes this page shows, the way the document page
  // made the process's clientHash (the same bytes, its algorithm: the
  // process's documentHashAlgorithm). Worked out here, never taken from the
  // gateway: its documentHash only has to start with the same 16 digits, else
  // no code (null).
  codeOf = async (bytes) => {
    const p = this.state.process;
    const algorithm = signService.pickHashAlgorithm([p.documentHashAlgorithm || "SHA-256"]);
    if (!algorithm) return null;
    try {
      const hex = await signService.digestHex(algorithm, bytes);
      return String(p.documentHash || "").toLowerCase().startsWith(hex.slice(0, 16)) ? codeFromDigest(hex) : null;
    } catch (e) {
      return null;
    }
  };

  // read the bytes again and work the code out anew
  retryCode = () => {
    this.bytesRequested = false;
    this.setState({ docFile: null, documentCode: null, codeProblem: false });
  };

  // the process as an answer returns it, with its version
  takeProcess = (res, extra) =>
    this.setState({
      process: res.data,
      etag: (res.headers && res.headers.etag) || this.state.etag,
      busy: null,
      ...extra,
    });

  // fixed position: an empty body, the gateway takes it from the options
  place = () => {
    const fixed = this.fixedPlacement();
    if (!fixed && !this.state.draft) return;
    this.setState({ busy: "place" });
    this.secureSubscription(
      signService
        .placeSignature(this.processId(), this.state.etag, fixed ? null : this.state.draft)
        .subscribe(
          (res) => {
            this.takeProcess(res, { editing: false });
            this.loadProcess(true); // confirmation shows what the server holds
          },
          (e) => {
            this.setState({ busy: null });
            this.showError(e, "faceSign.placeError");
            if (e && e.response && e.response.status === 412) this.loadProcess(true);
          }
        )
    );
  };

  finishSigning = () => {
    this.setState({ busy: "finish", signerDeactivated: false });
    this.secureSubscription(
      signService.finish(this.processId()).subscribe(
        (res) => this.takeProcess(res),
        (e) => {
          this.setState({ busy: null });
          if (!this.deactivatedMeanwhile(e)) this.showError(e, "faceSign.signError");
          this.loadProcess(true);
        }
      )
    );
  };

  // 400 SIGNER_DEACTIVATED at /finish or the eID launch: the signing tool was
  // deactivated after the process started; the process is kept - shown, to
  // come back to later or cancel. True when that was the refusal.
  deactivatedMeanwhile = (e) => {
    if (signService.problemCode(e) !== "SIGNER_DEACTIVATED") return false;
    this.setState({ signerDeactivated: true });
    return true;
  };

  // the gateway's own sentence for a refusal (its detail), or nothing
  detailOf = (e) => signService.problemMessage(e, "");

  refusedText = () => translateService.t("faceSign.engineRefused");

  // POST eid → EID_LAUNCHED with the one-time Unitool link, opened as given
  // (never logged); calling again hands out a new link. A share that no
  // longer lets the caller sign answers SIGNING_FAILED (GRANT_ENDED) instead.
  launchEid = () => {
    this.setState({ busy: "eid", eidNoApp: false, signerDeactivated: false });
    this.secureSubscription(
      signService.launchEid(this.processId()).subscribe(
        (res) => {
          const eid = res.data && res.data.eid;
          this.takeProcess(res, { eid: eid || null, now: Date.now() });
          if (eid && eid.launchUrl)
            customProtocolCheck(eid.launchUrl, () => this.setState({ eidNoApp: true }), () => {}, EID_APP_WAIT_MS);
        },
        (e) => {
          this.setState({ busy: null });
          if (!this.deactivatedMeanwhile(e)) this.showError(e, "faceSign.eidLaunchError");
          this.loadProcess(true);
        }
      )
    );
  };

  cancelProcess = () => {
    this.setState({ busy: "cancel" });
    this.secureSubscription(
      signService.cancel(this.processId()).subscribe(
        (res) => this.takeProcess(res, { confirmCancel: false }),
        (e) => {
          this.setState({ busy: null, confirmCancel: false });
          this.showError(e, "faceSign.cancelError");
          this.loadProcess(true);
        }
      )
    );
  };

  // the details page of the document; dashboardView sends non-active users
  // to the landing page, so they get viewResult (face runbook F6.2)
  openDocument = () => {
    const docId = this.state.process && this.state.process.docId;
    if (!docId) return this.goBack();
    // a link session stays on viewResult: dashboardView would end it
    if (authService.isActive() && !linkSession.has())
      routingService.push(routingService.routeNames.dashboardView, { params: { docId } });
    else routingService.push(routingService.routeNames.viewResult, { params: { id: docId } });
  };

  visualOptions = () => (this.state.process && this.state.process.visualSignature) || null;

  fixedPlacement = () => {
    const v = this.visualOptions();
    return v && v.mandatory && v.placement ? v.placement : null;
  };

  box = () => {
    const d = this.visualOptions() && this.visualOptions().dimensions;
    if (d && d.width > 0 && d.height > 0) return { width: d.width, height: d.height };
    return this.isEid() ? EID_BOX : DEFAULT_BOX;
  };

  // The facsimile eID's stamp carries: signed in the account's, through a
  // share link the one made for that link (userInfo.facsimilePath, kept in
  // the AppContext). The gateway reads it itself at the launch: the website
  // never sends the image or its URL there.
  facsimile = () => {
    const app = (this.context && this.context.state) || {};
    return app.facsimile && app.useFacsimile > 0 ? app.facsimile : null;
  };

  // the stamp's lines under the facsimile, as the gateway will draw them:
  // the line and the time (here: now; the stamp carries the signing time)
  eidCaption = () => [EID_STAMP_LINE, dateHelperService.format(new Date(), "dd.MM.yyyy HH:mm")];

  // the existing editor in create mode (type the name, pick a font card);
  // saving it puts the facsimile in userInfo and the AppContext, and the box
  // shows it at once. Through a share link it is made for that link alone.
  createFacsimile = () =>
    this.context.setState({
      showFacsimileModal: true,
      fromDashboard: false,
      isEditing: false,
      signatureProcessStep: 1,
      faximCardInitialized: true,
    });

  // names in the document's own script when it has them, else Latin
  signatoryName = () => {
    const sg = (this.state.process && this.state.process.signatory) || {};
    const local = [sg.firstNameLocal, sg.lastNameLocal].filter(Boolean).join(" ");
    const latin = [sg.firstName, sg.lastName].filter(Boolean).join(" ");
    return local || latin;
  };

  onIdentomatDone = () => {
    this.setState({ submitted: true });
    this.checkVerification();
  };

  processId = () => this.props.match?.params?.processId;

  // quiet: refresh without replacing the page by the loader (keeps the frame)
  loadProcess = (quiet = false) => {
    if (!quiet) this.setState({ loading: true });
    this.secureSubscription(
      signService.getProcess(this.processId()).subscribe(
        (res) => {
          this.setState({
            process: res.data,
            etag: (res.headers && res.headers.etag) || null,
            loading: false,
          });
          this.loadDocument(res.data);
          // reload during the verification: the notice was accepted already;
          // /verification is repeatable and hands the same widget back
          if (
            res.data &&
            res.data.status === "VERIFICATION_STARTED" &&
            !this.state.verification &&
            !this.state.starting
          )
            this.startVerification();
        },
        (e) => {
          this.setState({ loading: false });
          this.showError(e, "faceSign.loadError");
        }
      )
    );
  };

  chooseAuthType = (authType) => {
    if (this.state.choosing) return;
    this.setState({ choosing: authType });
    this.secureSubscription(
      signService.setAuthType(this.processId(), this.state.etag, authType).subscribe(
        (res) =>
          this.setState({
            process: res.data,
            etag: (res.headers && res.headers.etag) || this.state.etag,
            choosing: null,
          }),
        (e) => {
          this.setState({ choosing: null });
          if (signService.problemCode(e) === "SIGNER_NOT_PROVISIONED") return this.setState({ notProvisioned: this.detailOf(e) });
          this.showError(e, "faceSign.authTypeError");
          // 412 PROCESS_CHANGED: read the process again before another try
          if (e && e.response && e.response.status === 412) this.loadProcess();
        }
      )
    );
  };

  // A step the process's status no longer allows (409 PROCESS_STATE_CONFLICT:
  // it moved on, or ended - EXPIRED past its window) is no error to show:
  // the process, read again, says by its status what happened.
  // Never logged: the process carries the signatory's identity in full.
  showError = (e, fallbackKey) => {
    if (signService.problemCode(e) === "PROCESS_STATE_CONFLICT") return this.loadProcess(true);
    stateService.event(
      stateEvents.globalError,
      signService.problemMessage(e, translateService.t(fallbackKey))
    );
  };

  // a document link's session goes back to its document (leaving it would
  // end the link's session)
  goBack = () =>
    linkSession.has() && this.state.process && this.state.process.docId
      ? this.openDocument()
      : routingService.push(routingService.routeNames.dashboard);

  // ISO date (2031-06-28) → 28.06.2031
  formatDate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso || null;
  };

  // signer type and auth labels are shared with the Signature page
  label = (prefix, value, namespace = "signature") => {
    const key = `${namespace}.${prefix}_${value}`;
    const text = translateService.t(key);
    return text && text !== key ? text : value;
  };

  // the signing tool as the Sign menu shows it: its name (for eID the card)
  // and kind, and under them what it means
  renderSigner(signer) {
    const method = signerMethod(signer);
    const kind = methodKind(method);
    return (
      <React.Fragment>
        <dt>{translateService.t("signature.name")}</dt>
        <dd>
          {methodTitle(method)}
          {kind && ` · ${kind}`}
          <span className="sign-process__signer-hint">{methodHint(method)}</span>
        </dd>
      </React.Fragment>
    );
  }

  // What the signatory compares with the consent inside the verification,
  // labelled as the consent labels it: the document code (worked out here),
  // and the signing tool and its key exactly as the gateway gives them
  // (signingTool; absent without a certificate: neither line). The tool's
  // description stays under its name, as in the Sign menu.
  renderCodes(process) {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const tool = process.signingTool;
    const { documentCode, codeProblem } = this.state;
    let code = <span className="sign-process__muted">…</span>;
    if (documentCode) code = <span className="sign-process__code">{documentCode}</span>;
    else if (codeProblem)
      code = (
        <span className="sign-process__code-problem" role="alert">
          {t("documentCodeError")}{" "}
          <button type="button" className="sign-process__link" onClick={this.retryCode}>
            {t("tryAgain")}
          </button>
        </span>
      );
    return (
      <React.Fragment>
        <dt>{t("documentCode")}</dt>
        <dd>{code}</dd>
        {tool && (
          <React.Fragment>
            <dt>{t("signingTool")}</dt>
            <dd>
              {tool.name}
              {process.signer && <span className="sign-process__signer-hint">{methodHint(signerMethod(process.signer))}</span>}
            </dd>
            <dt>{t("signingToolKey")}</dt>
            <dd>
              <span className="sign-process__code">{tool.keyFingerprint}</span>
            </dd>
          </React.Fragment>
        )}
        <dd className="sign-process__compare">{t("compareHint")}</dd>
      </React.Fragment>
    );
  }

  renderAuth(process) {
    const authType = process.authType;
    if (authType) {
      return (
        <React.Fragment>
          <dt>{translateService.t("signature.authTypes")}</dt>
          <dd>{this.label("auth", authType)}</dd>
        </React.Fragment>
      );
    }
    return null;
  }

  renderNext(process) {
    const status = process.status;
    if (status === "INPUT_NEEDED") return this.renderChoice(process);
    // not the required signatory, whatever the method (face or eID); final.
    // Who it was is never shown; a new process, for the right person, starts
    // from the document
    if (status === "SIGNATORY_MISMATCH")
      return this.renderPanel(translateService.t("faceSign.signatoryMismatch"), {
        warning: true,
        button: translateService.t("faceSign.toDocument"),
        onClick: this.openDocument,
      });
    if (["SIGNING", "SIGNED", "SIGNING_FAILED", "CANCELLED", "EXPIRED"].includes(status))
      return this.renderOutcome(status);
    if (process.authType === "EID") {
      if (status === "EID_LAUNCHED") return this.renderEidLaunched();
      // a mark to place and no facsimile yet: make one first, or go on without
      if (status === "INITIAL" && this.needsPlacement() && !this.facsimile() && !this.state.skipFacsimile)
        return this.renderNoFacsimile();
      if (status === "INITIAL" || status === "PLACED")
        return this.needsPlacement() ? this.renderPlacement() : this.renderEidConfirm();
    }
    if (status === "VERIFIED" || status === "PLACED") {
      const confirm = (status === "PLACED" && !this.state.editing) || (status === "VERIFIED" && this.state.noMark);
      return confirm ? this.renderConfirm() : this.renderPlacement();
    }
    if (process.authType === "FACE") return this.renderFace();
    return (
      <p className="sign-process__pending">
        {translateService.t("faceSign.authUnsupported")}
      </p>
    );
  }

  // a message with at most one button (Try again, Start again, Back)
  renderPanel(text, { warning = false, button = null, onClick = null, loading = false } = {}) {
    return (
      <div className="sign-process__step">
        {text && (
          <p className={warning ? "sign-process__warning" : "sign-process__pending"}>{text}</p>
        )}
        {button && (
          <div className="buttons">
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              loading={loading}
              onClick={onClick}
            >
              {button}
            </LoadingButton>
          </div>
        )}
      </div>
    );
  }

  // another verification attempt may start ("Try again", "Start again")
  attemptsLeft = (process) => {
    const progress = process.verification;
    return !progress || progress.attempt < progress.maxAttempts;
  };

  // Cancel: the statuses that can still progress; a rejected or expired
  // verification exactly while "Try again" / "Start again" is offered
  cancellable = (process) => {
    if (CANCELLABLE.includes(process.status)) return true;
    if (this.state.exhausted || process.authType !== "FACE" || !this.attemptsLeft(process)) return false;
    if (process.status === "VERIFICATION_EXPIRED") return true;
    return process.status === "VERIFICATION_REJECTED" && !!(process.rejectReason && process.rejectReason.retryable);
  };

  renderFace() {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const process = this.state.process;
    const attemptsLeft = this.attemptsLeft(process);
    const again = (key) => ({
      button: t(key),
      onClick: this.startVerification,
      loading: this.state.starting,
    });
    const back = { button: t("toDashboard"), onClick: this.goBack };
    const exhausted = () => this.renderPanel(t("attemptsExhausted"), { warning: true, ...back });

    if (this.state.exhausted) return exhausted();

    switch (process.status) {
      case "VERIFIED":
        return this.renderPanel(t("verified"));
      case "VERIFICATION_UNDECIDED":
        return this.renderPanel(t("undecided"), { warning: true, ...back });
      case "VERIFICATION_REJECTED": {
        // the gateway words the reason in the user's language (Accept-Language)
        const reason = process.rejectReason || {};
        const text = reason.message || t("rejected");
        if (reason.retryable && attemptsLeft)
          return this.renderPanel(text, { warning: true, ...again("tryAgain") });
        const after = reason.retryable ? t("attemptsExhausted") : t("startOver");
        return this.renderPanel(`${text} ${after}`, { warning: true, ...back });
      }
      case "VERIFICATION_EXPIRED":
        return attemptsLeft
          ? this.renderPanel(t("sessionExpired"), again("startAgain"))
          : exhausted();
      case "VERIFICATION_STARTED":
        return this.renderWidget();
      default:
        return this.renderNotice(); // INITIAL
    }
  }

  // VERIFICATION_STARTED: the widget, or getting it back after a reload
  renderWidget() {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const v = this.state.verification;
    if (!v)
      return this.renderPanel("", {
        button: t("resume"),
        onClick: this.startVerification,
        loading: this.state.starting,
      });

    const left = v.expiresAt - this.state.now;
    if (left <= 0)
      // the gateway reports VERIFICATION_EXPIRED up to two minutes later;
      // the page need not wait (POST verification opens the next attempt)
      return v.attempt < v.maxAttempts
        ? this.renderPanel(t("sessionExpired"), {
            button: t("startAgain"),
            onClick: this.startVerification,
            loading: this.state.starting,
          })
        : this.renderPanel(t("attemptsExhausted"), {
            warning: true,
            button: t("toDashboard"),
            onClick: this.goBack,
          });

    const trouble = this.state.checkTrouble && (
      <p className="sign-process__warning" aria-live="polite">{t("checkTrouble")}</p>
    );
    if (this.state.submitted)
      return (
        <React.Fragment>
          {this.renderPanel(t("submitted"))}
          {trouble}
        </React.Fragment>
      );

    const mm = Math.floor(left / 60000);
    const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, "0");
    const engine = this.engineLabel(v.engine);
    return (
      <div className="sign-process__step">
        <div className="sign-process__meta">
          <span>{translateService.t("faceSign.attempt", v.attempt, v.maxAttempts)}</span>
          {engine && <span>{translateService.t("faceSign.engine", engine)}</span>}
          <span className={left < WARN_BEFORE_MS ? "expiring" : ""} aria-live="polite">
            {t("expiresIn")} {mm}:{ss}
          </span>
        </div>
        {trouble}
        {this.renderSession(v)}
      </div>
    );
  }

  // How the page runs the engine, by the session's handshake (whatever the
  // engine): IFRAME_POSTMESSAGE frames the widget and takes DONE from the
  // session's widgetOrigin only; APP_LAUNCH opens it; NONE (or a handshake
  // this site does not know) waits for the polled check. A session without a
  // handshake (an older gateway) is framed.
  renderSession(v) {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const handshake = v.handshake || "IFRAME_POSTMESSAGE";
    if (handshake === "IFRAME_POSTMESSAGE")
      return <IdentomatFrame widgetUrl={v.widgetUrl} origin={v.widgetOrigin} title={t("frameTitle")} onDone={this.onIdentomatDone} />;
    if (handshake === "APP_LAUNCH")
      return this.renderPanel(t("verifyElsewhere"), {
        button: t("openVerification"),
        onClick: () => window.open(v.widgetUrl, "_blank", "noopener"),
      });
    return this.renderPanel(t("verifyElsewhere"));
  }

  // an engine's label from the catalogue, never its name; null when unknown
  engineLabel = (engine) => {
    const entry = (this.state.engines || []).find((e) => e.engine === engine);
    return entry ? entry.label : null;
  };

  // the set-up refusals, above the step: a signing tool not set up yet (the
  // owner gets the way to its card, a member the advice to ask the owner),
  // its engine refusing or unreachable (try again when asked, never by
  // itself), a signing tool deactivated after the start
  renderProblems() {
    const t = (k, ...a) => translateService.t(`faceSign.${k}`, ...a);
    const { notProvisioned, verificationProblem, signerDeactivated, process } = this.state;
    const owner = !linkSession.has() && !!process && sessionService.owns(process.workspaceId);
    return (
      <React.Fragment>
        {notProvisioned !== null && (
          <div className="sign-process__step">
            <p className="sign-process__warning">
              {notProvisioned ? t("notProvisioned", notProvisioned) : t("notProvisionedPlain")}{" "}
              {t(owner ? "notProvisionedOwner" : "notProvisionedMember")}
            </p>
            {owner && (
              <div className="buttons">
                <LoadingButton type="button" className="button background-color-two color-white" onClick={openSigningTools}>
                  {t("openSigningTools")}
                </LoadingButton>
              </div>
            )}
          </div>
        )}
        {verificationProblem &&
          this.renderPanel(verificationProblem.text, {
            warning: true,
            button: t("retryLater"),
            loading: this.state.starting,
            onClick: () => {
              if (verificationProblem.retry === "check") {
                this.setState({ checkBlocked: false, verificationProblem: null });
                this.checkVerification();
              } else this.startVerification();
            },
          })}
        {signerDeactivated && this.renderPanel(t("signerDeactivated"), { warning: true })}
      </React.Fragment>
    );
  }

  // F5: where the visible signature goes
  renderPlacement() {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const process = this.state.process;
    const fixed = this.fixedPlacement();
    const options = this.visualOptions();
    const optional = !(options && options.mandatory);
    const value = fixed || this.state.draft || process.placement || null;
    return (
      <div className="sign-process__step">
        <p className="sign-process__pending">{t(fixed ? "placeFixed" : "placeHint")}</p>
        {this.state.docFile ? (
          <PlacementView
            file={this.state.docFile}
            box={this.box()}
            label={this.signatoryName() || t("eidMark")}
            image={this.isEid() ? this.facsimile() : null}
            caption={this.isEid() ? this.eidCaption() : null}
            value={value}
            fixed={!!fixed}
            ariaLabel={t("placeBox")}
            onChange={(draft) => this.setState({ draft })}
          />
        ) : (
          <img src="./assets/images/black-loading.svg" className="content-loader" />
        )}
        <div className="buttons">
          <LoadingButton
            type="button"
            className="button background-color-two color-white"
            loading={this.state.busy === "place"}
            disabled={!value || !!this.state.busy}
            onClick={this.place}
          >
            {t("continue")}
          </LoadingButton>
          {optional && process.status === (this.isEid() ? "INITIAL" : "VERIFIED") && (
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={!!this.state.busy}
              onClick={() => {
                this.setState({ noMark: true });
                this.loadProcess(true);
              }}
            >
              {t("withoutMark")}
            </LoadingButton>
          )}
          {this.state.editing && (
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={!!this.state.busy}
              onClick={() => this.setState({ editing: false, draft: null })}
            >
              {t("back")}
            </LoadingButton>
          )}
        </div>
      </div>
    );
  }

  // F6: what is about to be signed, as the server holds it
  renderConfirm() {
    const t = (k, ...a) => translateService.t(`faceSign.${k}`, ...a);
    const process = this.state.process;
    const sg = process.signatory || {};
    const latin = [sg.firstName, sg.lastName].filter(Boolean).join(" ");
    const name = this.signatoryName();
    const signer = process.signer || {};
    const placed = process.status === "PLACED" && process.placement;
    const wording =
      signer.type === "CLOUD_REMOTE_CA"
        ? t("keyModel_cert")
        : signer.type === "CLOUD_REMOTE_SEAL_SIG"
        ? t("keyModel_sealSig", signer.name)
        : null;
    return (
      <div className="sign-process__step sign-process__confirm">
        <p className="sign-process__pending">{t("confirmTitle")}</p>
        <dl className="sign-process__facts">
          <dt>{t("document")}</dt>
          <dd>{(this.state.doc && this.state.doc.name) || process.docId}</dd>
          <dt>{t("signingAs")}</dt>
          <dd>
            {name}
            {latin && latin !== name ? ` (${latin})` : ""}
          </dd>
          {/* who issued the personal number, and from which document */}
          {[
            ["personalId", sg.personalId],
            ["issuingCountry", sg.issuingCountry],
            ["documentType", sg.documentType && this.label("docType", sg.documentType, "faceSign")],
            ["documentNumber", sg.documentNumber],
            ["issueDate", this.formatDate(sg.issueDate)],
            ["expirationDate", this.formatDate(sg.expirationDate)],
            // unmasked since 2026-10-15, as the gateway answers it
            ["birthDate", this.formatDate(sg.birthDate)],
          ]
            .filter(([, value]) => value)
            .map(([key, value]) => (
              <React.Fragment key={key}>
                <dt>{t(key)}</dt>
                <dd>{value}</dd>
              </React.Fragment>
            ))}
          <dt>{t("visibleSignature")}</dt>
          <dd>{placed ? t("onPage", process.placement.page) : t("noVisible")}</dd>
        </dl>
        {wording && <p className="sign-process__wording">{wording}</p>}
        {/* a cloud signing tool: what the validation will show, before Sign */}
        {isCloudSigner(process.signer) && <CloudFlowNotice />}
        <div className="buttons">
          <LoadingButton
            type="button"
            className="button background-color-two color-white"
            loading={this.state.busy === "finish"}
            disabled={!!this.state.busy}
            onClick={this.finishSigning}
          >
            {t("sign")}
          </LoadingButton>
          {placed && !this.fixedPlacement() && (
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={!!this.state.busy}
              onClick={() => this.setState({ editing: true, draft: process.placement })}
            >
              {t("changePosition")}
            </LoadingButton>
          )}
          {this.state.noMark && process.status === "VERIFIED" && (
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={!!this.state.busy}
              onClick={() => this.setState({ noMark: false })}
            >
              {t("addMark")}
            </LoadingButton>
          )}
        </div>
      </div>
    );
  }

  // eID with a mark to place and no facsimile yet: the editor in create mode,
  // or "Continue without facsimile" - without a visible signature where it
  // is optional, with the stamp's text alone where the share fixes it
  renderNoFacsimile() {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const fixed = !!this.fixedPlacement();
    const optional = !(this.visualOptions() && this.visualOptions().mandatory);
    const without = () => {
      if (optional && !fixed) {
        this.setState({ noMark: true });
        this.loadProcess(true);
      } else this.setState({ skipFacsimile: true });
    };
    return (
      <div className="sign-process__step">
        <p className="sign-process__pending">
          {t(optional && !fixed ? "eidNoFacsimileOptional" : "eidNoFacsimileFixed")}
        </p>
        <div className="buttons">
          <LoadingButton type="button" className="button background-color-two color-white" onClick={this.createFacsimile}>
            {t("createFacsimile")}
          </LoadingButton>
          <LoadingButton type="button" className="button background-color-one color-white" onClick={without}>
            {translateService.t("dashboard.withoutApost")}
          </LoadingButton>
        </div>
      </div>
    );
  }

  // eID from INITIAL or PLACED: what is about to be signed, then Unitool
  renderEidConfirm() {
    const t = (k, ...a) => translateService.t(`faceSign.${k}`, ...a);
    const process = this.state.process;
    const placed = process.status === "PLACED" && process.placement;
    return (
      <div className="sign-process__step sign-process__confirm">
        <p className="sign-process__pending">{t("eidConfirmTitle")}</p>
        <dl className="sign-process__facts">
          <dt>{t("document")}</dt>
          <dd>{(this.state.doc && this.state.doc.name) || process.docId}</dd>
          <dt>{t("visibleSignature")}</dt>
          <dd>{placed ? t("onPage", process.placement.page) : t("noVisible")}</dd>
        </dl>
        <p className="sign-process__wording">{t("eidHint")}</p>
        <div className="buttons">
          <LoadingButton
            type="button"
            className="button background-color-two color-white"
            loading={this.state.busy === "eid"}
            disabled={!!this.state.busy}
            onClick={this.launchEid}
          >
            {t("eidLaunch")}
          </LoadingButton>
          {placed && !this.fixedPlacement() && (
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={!!this.state.busy}
              onClick={() => this.setState({ editing: true, draft: process.placement })}
            >
              {t("changePosition")}
            </LoadingButton>
          )}
          {this.state.noMark && process.status === "INITIAL" && (
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={!!this.state.busy}
              onClick={() => this.setState({ noMark: false })}
            >
              {t("addMark")}
            </LoadingButton>
          )}
        </div>
      </div>
    );
  }

  // EID_LAUNCHED: Unitool has the link; the page polls until it signed
  renderEidLaunched() {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const eid = this.state.eid;
    const left = eid ? eid.expiresAt - this.state.now : 0;
    const mm = Math.floor(Math.max(left, 0) / 60000);
    const ss = String(Math.floor((Math.max(left, 0) % 60000) / 1000)).padStart(2, "0");
    return (
      <div className="sign-process__step">
        <p className="sign-process__pending" aria-live="polite">
          {eid && left > 0 ? t("eidWaiting") : t("eidExpired")}
        </p>
        {eid && left > 0 && (
          <div className="sign-process__meta">
            <span className={left < WARN_BEFORE_MS ? "expiring" : ""}>
              {t("eidExpiresIn")} {mm}:{ss}
            </span>
          </div>
        )}
        {this.state.eidNoApp && (
          <p
            className="sign-process__warning"
            dangerouslySetInnerHTML={{ __html: translateService.t("dashboard.no-driver") }}
          />
        )}
        <div className="buttons">
          <LoadingButton
            type="button"
            className="button background-color-two color-white"
            loading={this.state.busy === "eid"}
            disabled={!!this.state.busy}
            onClick={this.launchEid}
          >
            {t("eidLaunchAgain")}
          </LoadingButton>
        </div>
      </div>
    );
  }

  // SIGNING (polled), SIGNED, SIGNING_FAILED, CANCELLED, EXPIRED
  renderOutcome(status) {
    const t = (k) => translateService.t(`faceSign.${k}`);
    const open = { button: t("openDocument"), onClick: this.openDocument };
    switch (status) {
      case "SIGNING":
        return (
          <div className="sign-process__step">
            <p className="sign-process__pending" aria-live="polite">{t("signing")}</p>
            <img src="./assets/images/black-loading.svg" className="content-loader" />
          </div>
        );
      case "SIGNED":
        return (
          <React.Fragment>
            {this.renderPanel(t("signed"), open)}
            {isCloudSigner(this.state.process.signer) && <CloudResultNotice />}
          </React.Fragment>
        );
      case "SIGNING_FAILED": {
        // final; the gateway's reason (rejectReason.message), else a generic
        // text. GRANT_ENDED: the sharer changed the share - the document page
        // reads it and its signing options again, and offers what is left
        const reason = this.state.process.rejectReason || {};
        if (reason.code === "GRANT_ENDED")
          return this.renderPanel(t("grantEnded"), { warning: true, button: t("toDocument"), onClick: this.openDocument });
        return this.renderPanel(reason.message || t("signFailed"), { warning: true, ...open });
      }
      // final: no step within the process's window; a new signing starts
      // from the document
      case "EXPIRED":
        return this.renderPanel(t("timeRanOut"), { warning: true, button: t("toDocument"), onClick: this.openDocument });
      default:
        return this.renderPanel(t("cancelled"), open);
    }
  }

  // until signing starts; a second click confirms
  renderCancel(process) {
    if (!this.cancellable(process)) return null;
    const t = (k) => translateService.t(`faceSign.${k}`);
    return (
      // red: the app's colour for leaving a flow (background-color-three)
      <div className="sign-process__cancel buttons">
        {this.state.confirmCancel ? (
          <React.Fragment>
            <span>{t("cancelConfirm")}</span>
            <LoadingButton
              type="button"
              className="button background-color-three color-white"
              loading={this.state.busy === "cancel"}
              disabled={!!this.state.busy}
              onClick={this.cancelProcess}
            >
              {t("cancelYes")}
            </LoadingButton>
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={!!this.state.busy}
              onClick={() => this.setState({ confirmCancel: false })}
            >
              {t("cancelNo")}
            </LoadingButton>
          </React.Fragment>
        ) : (
          <LoadingButton
            type="button"
            className="button background-color-three color-white"
            disabled={!!this.state.busy}
            onClick={() => this.setState({ confirmCancel: true })}
          >
            {t("cancelSigning")}
          </LoadingButton>
        )}
      </div>
    );
  }

  // biometric notice (face runbook §1.5); draft text until counsel confirms.
  // Continue = acceptance: the gateway records it when verification starts.
  // Continue waits for the document code: the signatory compares it with the
  // consent inside the verification.
  renderNotice() {
    const t = (k) => translateService.t(`faceSign.notice_${k}`);
    return (
      <div className="sign-process__step sign-process__notice">
        <p className="sign-process__pending">{t("title")}</p>
        <ul>
          <li>{t("what")}</li>
          <li>{t("who")}</li>
          <li>{t("purpose")}</li>
          <li>{t("retention")}</li>
          <li>{t("rights")}</li>
        </ul>
        {this.state.cameraDenied && (
          <p className="sign-process__warning">{translateService.t("faceSign.cameraDenied")}</p>
        )}
        {IN_APP_BROWSER.test(navigator.userAgent) && (
          <p className="sign-process__warning">{translateService.t("faceSign.inAppBrowser")}</p>
        )}
        <label className="sign-process__accept">
          <input
            type="checkbox"
            checked={this.state.noticeRead}
            onChange={(e) => this.setState({ noticeRead: e.target.checked })}
          />
          {t("accept")}
        </label>
        <div className="buttons">
          <LoadingButton
            type="button"
            className="button background-color-two color-white"
            loading={this.state.starting}
            disabled={!this.state.noticeRead || !this.state.documentCode}
            onClick={this.startVerification}
          >
            {translateService.t("faceSign.continue")}
          </LoadingButton>
        </div>
      </div>
    );
  }

  // status INPUT_NEEDED: the signatory picks one of the signer's methods
  renderChoice(process) {
    const offered = (process.signer && process.signer.signatoryAuthTypes) || [];
    return (
      <div className="sign-process__choose">
        <p className="sign-process__pending">{translateService.t("faceSign.chooseAuth")}</p>
        <div className="buttons">
          {offered.map((a) => {
            const usable = BROWSER_AUTH_TYPES.includes(a);
            return (
              <LoadingButton
                key={a}
                type="button"
                className="button background-color-two color-white"
                loading={this.state.choosing === a}
                disabled={!usable || !!this.state.choosing}
                title={usable ? undefined : translateService.t("faceSign.authNotHere")}
                onClick={() => this.chooseAuthType(a)}
              >
                {this.label("auth", a)}
              </LoadingButton>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    const { process, loading } = this.state;
    const comparing = this.comparesCodes();
    return (
      <div className={`package-info sign-process container ${translateService.key}`}>
        <div className="page-header">
          <span className="clickable" onClick={this.goBack}>
            <img src="../../assets/images/close32.svg" />
          </span>
        </div>
        <div className="package-info-items">
          <div className="flex-title-item">
            <div className="title-container">
              <span className="title">{translateService.t("faceSign.processTitle")}</span>
            </div>
          </div>
          {loading ? (
            <img src="./assets/images/black-loading.svg" className="content-loader" />
          ) : (
            process && (
              <React.Fragment>
                <dl className="sign-process__facts">
                  {this.state.doc && (
                    <React.Fragment>
                      <dt>{translateService.t("faceSign.document")}</dt>
                      <dd>{this.state.doc.name}</dd>
                    </React.Fragment>
                  )}
                  {comparing && this.renderCodes(process)}
                  <dt>{translateService.t("faceSign.process")}</dt>
                  <dd>{process.processId}</dd>
                  <dt>{translateService.t("faceSign.status")}</dt>
                  <dd>{process.status}</dd>
                  {process.signer && !(comparing && process.signingTool) && this.renderSigner(process.signer)}
                  {this.renderAuth(process)}
                  <dt>{translateService.t("faceSign.documentHash")}</dt>
                  <dd className="hash">
                    {process.documentHashAlgorithm} {process.documentHash}
                  </dd>
                </dl>
                {this.renderProblems()}
                {this.renderNext(process)}
                {this.renderCancel(process)}
              </React.Fragment>
            )
          )}
        </div>
      </div>
    );
  }
}
